import ky from "ky";
import type { RawCanvas } from "../../../shared/types";

// Optimized Binary Compression (Native)
const compressData = async (data: unknown): Promise<Uint8Array> => {
  const stream = new Blob([JSON.stringify(data)])
    .stream()
    .pipeThrough(new CompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

// Optimized Binary Decompression (Native)
const decompressData = async (input: ArrayBuffer): Promise<string> => {
  const stream = new Response(input).body?.pipeThrough(new DecompressionStream("deflate"));
  if (!stream) throw new Error("Stream failed");
  return new Response(stream).text();
};

const clearSession = () => {
  localStorage.removeItem("accessToken");
  // Trigger storage event for the current window too
  window.dispatchEvent(new Event("storage"));
  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

// The main API client for all requests.
const apiClient = ky.create({
  prefix: "/api",
  hooks: {
    beforeRequest: [
      ({ request }) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async ({ response }) => {
        if (response.status === 401) {
          clearSession();
        }
        return response;
      },
    ],
  },
});

export const signup = async (username: string, password: string): Promise<{ message: string }> => {
  return apiClient.post("signup", { json: { username, password } }).json();
};

export const login = async (
  username: string,
  password: string,
): Promise<{ accessToken: string }> => {
  const res = await apiClient
    .post("login", { json: { username, password } })
    .json<{ accessToken: string }>();
  localStorage.setItem("accessToken", res.accessToken);
  window.dispatchEvent(new Event("storage"));
  return res;
};

export const logout = async (): Promise<void> => {
  try {
    await apiClient.post("logout");
  } finally {
    clearSession();
  }
};

export const getCanvases = async (): Promise<RawCanvas[]> => {
  return apiClient.get("canvases").json();
};

export const createCanvas = async (): Promise<{ id: number }> => {
  return apiClient.post("canvases").json();
};

export const getCanvas = async (
  id: number,
): Promise<{
  data: string;
  current_turn_user_id: number;
  current_turn_username: string;
  owner: string;
  last_modified: string;
  last_viewport: string | null;
  is_sketching: boolean;
  collaborator_usernames: string;
}> => {
  const response = await apiClient.get(`canvases/${id}`);
  const res = await response.json<{
    data: string | null;
    current_turn_user_id: number;
    current_turn_username: string;
    owner: string;
    last_modified: string;
    last_viewport: string | null;
    is_sketching: boolean;
    collaborator_usernames: string;
  }>();

  if (res.data) {
    const bytes = Uint8Array.from(atob(res.data), (c) => c.charCodeAt(0));
    res.data = await decompressData(bytes.buffer);
  }

  return res as {
    data: string;
    current_turn_user_id: number;
    current_turn_username: string;
    owner: string;
    last_modified: string;
    last_viewport: string | null;
    is_sketching: boolean;
    collaborator_usernames: string;
  };
};

export const saveCanvas = async (
  id: number,
  data: { elements: unknown[]; appState: unknown },
  viewport?: string,
): Promise<{ message: string; last_modified: string }> => {
  const compressed = await compressData(data);
  const response = await apiClient.post(`canvases/${id}`, {
    body: compressed as BodyInit,
    headers: {
      "Content-Type": "application/octet-stream",
      ...(viewport && { "x-viewport": viewport }),
    },
  });
  return response.json();
};

export const setSketchingStatus = async (
  id: number,
  sketching: boolean,
): Promise<{ success: boolean }> => {
  return apiClient.post(`canvases/${id}/sketching`, { json: { sketching } }).json();
};

export const deleteCanvas = async (id: number): Promise<{ message: string }> => {
  return apiClient.delete(`canvases/${id}`).json();
};

export const inviteUser = async (
  canvasId: number,
  username: string,
): Promise<{ message: string }> => {
  return apiClient.post(`canvases/${canvasId}/invite`, { json: { username } }).json();
};

export const respondToInvitation = async (
  invitationId: number,
  status: "accepted" | "declined",
): Promise<{ message: string }> => {
  return apiClient.post(`invitations/${invitationId}`, { json: { status } }).json();
};

export const nextTurn = async (canvasId: number): Promise<RawCanvas> => {
  return apiClient.post(`canvases/${canvasId}/next-turn`).json();
};

export const saveSubscription = async (
  subscription: PushSubscription,
): Promise<{ message: string }> => {
  return apiClient.post("save-subscription", { json: subscription }).json();
};
